import { useState, useMemo } from "react";
import { Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import moment from "moment-timezone";
import {
  getInvoiceById,
  formatCurrency,
  INVOICE_STATUSES,
} from "./Api";

const STATUS_LOOKUP = INVOICE_STATUSES.reduce((acc, s) => {
  acc[s.value] = s;
  return acc;
}, {});

const StatusPill = ({ status, large }) => {
  const cfg = STATUS_LOOKUP[status] || STATUS_LOOKUP.Unpaid;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      borderRadius: 20,
      padding: large ? "6px 16px" : "3px 12px",
      fontSize: large ? "0.9rem" : "0.74rem",
      fontWeight: 700,
    }}>
      <span style={{ width: large ? 9 : 6, height: large ? 9 : 6, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
      {status}
    </span>
  );
};

const Section = ({ icon, title, badge, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="iv-drawer-section">
      <button type="button" className="iv-drawer-section__header" onClick={() => setOpen((p) => !p)}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FeatherIcon icon={icon} size={14} color="#4F46E5" />
          <span>{title}</span>
          {badge !== undefined && (
            <span className="iv-drawer-section__badge">{badge}</span>
          )}
        </div>
        <FeatherIcon icon={open ? "chevron-up" : "chevron-down"} size={14} color="#94A3B8" />
      </button>
      {open && <div className="iv-drawer-section__body">{children}</div>}
    </div>
  );
};

const InvoiceDetailDrawer = ({
  isOpen,
  onClose,
  invoice,
  canEdit,
  onChange,
  onForward,
  onEdit,
}) => {
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async () => {
    if (!invoice?._id) return;
    setRefreshing(true);
    try {
      const fresh = await getInvoiceById(invoice._id);
      if (fresh) onChange?.(fresh);
    } catch {
      // toast handled
    } finally {
      setRefreshing(false);
    }
  };

  const timeline = useMemo(() => {
    if (!invoice) return [];
    const items = [];
    (invoice.statusHistory || []).forEach((s) => items.push({
      kind: "status",
      at: s.changedAt,
      from: s.from,
      to: s.to,
      note: s.note,
      by: s.changedBy?.displayName || "Unknown",
      byRole: s.changedBy?.role || "",
    }));
    (invoice.communicationLog || []).forEach((c) => items.push({
      kind: "comm",
      at: c.sentAt,
      channel: c.channel,
      recipients: c.recipients,
      subject: c.subject,
      message: c.message,
      attachmentsIncluded: c.attachmentsIncluded,
      success: c.success,
      by: c.sentBy?.displayName || "Unknown",
      byRole: c.sentBy?.role || "",
    }));
    return items.sort((a, b) => new Date(b.at) - new Date(a.at));
  }, [invoice]);

  if (!isOpen || !invoice) return null;

  const fmt = (d, withTime = true) =>
    d ? moment(d).format(withTime ? "MMM D, YYYY · h:mm A" : "MMM D, YYYY") : "—";
  const fmtDate = (d) => (d ? moment.utc(d).format("MMM D, YYYY") : "—");
  const fmtSubmitted = (inv) => {
    const d = inv?.createdAt || inv?.invoiceDate;
    return d ? moment(d).format("MMM D, YYYY") : "—";
  };

  return (
    <>
      <div className={`iv-drawer-backdrop${isOpen ? " is-open" : ""}`} onClick={onClose} />
      <aside className={`iv-drawer${isOpen ? " is-open" : ""}`}>
        <header className="iv-drawer__header">
          <span className="iv-drawer__no">#{invoice.invoiceNo || "—"}</span>
          <StatusPill status={invoice.status} />

          <h3 className="iv-drawer__title" title={invoice.title || "Untitled invoice"}>
            {invoice.title || "Untitled invoice"}
          </h3>

          <span className="iv-drawer__vendor">
            <FeatherIcon icon="briefcase" size={13} />
            {invoice.vendorName || "—"}
          </span>
          {invoice.referenceNo && (
            <span className="iv-drawer__ref">Ref {invoice.referenceNo}</span>
          )}

          <span className="iv-drawer__fact">
            <FeatherIcon icon="calendar" size={13} />
            <i>Submitted</i>{fmtSubmitted(invoice)}
          </span>
          <span className="iv-drawer__fact">
            <FeatherIcon icon="clock" size={13} />
            <i>Due</i>{fmtDate(invoice.dueDate)}
          </span>
          {invoice.paidAt && (
            <span className="iv-drawer__fact iv-drawer__fact--paid">
              <FeatherIcon icon="check-circle" size={13} />
              <i>Paid</i>{fmtDate(invoice.paidAt)}
            </span>
          )}

          <span className="iv-drawer__amount">{formatCurrency(invoice.amount, invoice.currency)}</span>

          <div className="iv-drawer__head-actions">
            <button type="button" className="iv-icon-btn" title="Refresh" onClick={refresh} disabled={refreshing}>
              {refreshing ? <Spinner size="sm" /> : <FeatherIcon icon="refresh-cw" size={14} />}
            </button>
            {canEdit && (
              <button type="button" className="iv-icon-btn" title="Edit" onClick={onEdit}>
                <FeatherIcon icon="edit-2" size={14} />
              </button>
            )}
            {canEdit && (
              <button type="button" className="iv-icon-btn iv-icon-btn--green" title="Forward" onClick={onForward}>
                <FeatherIcon icon="send" size={14} />
              </button>
            )}
            <button type="button" className="iv-icon-btn" title="Close" onClick={onClose}>
              <FeatherIcon icon="x" size={14} />
            </button>
          </div>
        </header>

        <div className="iv-drawer__body">
          {/* Comments (read-only — new comments are added from the table) */}
          <Section icon="message-square" title="Comments" badge={invoice.commentsHistory?.length || 0}>
            {(invoice.commentsHistory || []).length === 0 ? (
              <div style={{ color: "#94A3B8", fontSize: "0.82rem", textAlign: "center", padding: 12 }}>
                No comments yet.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {invoice.commentsHistory.slice().reverse().map((c, idx) => (
                  <div key={c._id || idx} className="iv-comment">
                    <div className="iv-comment__avatar">
                      {(c.author?.displayName || "?").charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="iv-comment__meta">
                        <strong>{c.author?.displayName || "Unknown"}</strong>
                        <span>{c.author?.role || ""}</span>
                        <span>· {fmt(c.createdAt)}</span>
                      </div>
                      <div className="iv-comment__body">{c.comment}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Unified timeline (status + communications) */}
          <Section icon="activity" title="Activity & Communication Log" badge={timeline.length}>
            {timeline.length === 0 ? (
              <div style={{ color: "#94A3B8", fontSize: "0.82rem", textAlign: "center", padding: 12 }}>
                No activity yet.
              </div>
            ) : (
              <ol className="iv-timeline">
                {timeline.map((t, idx) => (
                  <li key={idx} className="iv-timeline__item">
                    <div className={`iv-timeline__dot iv-timeline__dot--${t.kind === "status" ? "status" : t.channel}`}>
                      <FeatherIcon
                        icon={
                          t.kind === "status"
                            ? "flag"
                            : t.channel === "email"
                              ? "mail"
                              : "message-circle"
                        }
                        size={11}
                        color="#fff"
                      />
                    </div>
                    <div className="iv-timeline__card">
                      <div className="iv-timeline__head">
                        <strong>
                          {t.kind === "status"
                            ? `Status changed to "${t.to}"`
                            : t.channel === "email"
                              ? "Email sent"
                              : "SMS sent"}
                        </strong>
                        <span>{fmt(t.at)}</span>
                      </div>
                      {t.kind === "status" && (
                        <div className="iv-timeline__body">
                          {t.from ? <>From <em>{t.from}</em> → <em>{t.to}</em><br /></> : null}
                          {t.note ? <span style={{ color: "#475569" }}>Note: {t.note}<br /></span> : null}
                          <span style={{ color: "#64748B" }}>By {t.by} ({t.byRole})</span>
                        </div>
                      )}
                      {t.kind === "comm" && (
                        <div className="iv-timeline__body">
                          {t.subject && <div><span>Subject:</span> {t.subject}</div>}
                          {t.message && (
                            <div style={{ color: "#475569", whiteSpace: "pre-wrap", margin: "4px 0" }}>
                              "{t.message}"
                            </div>
                          )}
                          <div style={{ marginTop: 4 }}>
                            <span>Recipients:</span>{" "}
                            {(t.recipients || []).map((r, i) => (
                              <span
                                key={i}
                                style={{
                                  display: "inline-block",
                                  marginRight: 4,
                                  padding: "1px 8px",
                                  borderRadius: 10,
                                  fontSize: "0.7rem",
                                  background: r.success ? "#ECFDF5" : "#FEF2F2",
                                  color: r.success ? "#065F46" : "#991B1B",
                                  border: r.success ? "1px solid #A7F3D0" : "1px solid #FECDD3",
                                }}
                                title={r.error || ""}
                              >
                                {r.target} {r.success ? "✓" : "✕"}
                              </span>
                            ))}
                          </div>
                          {t.attachmentsIncluded > 0 && (
                            <div style={{ color: "#64748B", marginTop: 2 }}>
                              📎 {t.attachmentsIncluded} attachment(s) included
                            </div>
                          )}
                          <div style={{ color: "#64748B", marginTop: 4 }}>By {t.by} ({t.byRole})</div>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Section>
        </div>
      </aside>
    </>
  );
};

export { InvoiceDetailDrawer };
